import React, { useEffect, useMemo, useState } from "react";
import SearchBar from "@/ui-components/search-bar";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import {
  VirtuosoWrapper,
  GroupingUtils,
} from "@/ui-components/grouped-virtuoso";
import { trackSearchSubmit, trackSearchResults } from "@/utils/gtm";
import { getDisplayTime } from "@/shared/api/firehose/models";
import type { NewsFeedItem } from "@/shared/api/firehose/models";
import { useWidgetSearchState } from "@/hooks/useWidgetSearchState";
import {
  buildContentHubTextFilter,
  type ContentHubItem,
  type ContentHubSource,
} from "@/features/content-hub";
import { CommonSearchProps } from "@/widget-components/newsquawk-headlines/HeadlinesSearchForm";
import { useContentHubFirehoseSearch } from "./hooks/useContentHubFirehoseSearch";
import { FirehoseHighlightItem } from "../firehose-all/components";
import { DateSectionHeader } from "./DateSectionHeader";

export interface ContentHubFirehoseSearchProps extends CommonSearchProps {
  /** The widget's content-hub backend_source(s) — the only per-widget input the
   *  shared search needs (single-source widgets pass one, combined views a group). */
  sources: ContentHubSource | readonly ContentHubSource[];
  defaultTitle?: string;
  /** Per-widget mapper to the legacy story model (truth-social passes its own;
   *  the rest use the default source-dispatched mapper). */
  mapItem?: (item: ContentHubItem) => NewsFeedItem;
}

/**
 * Header text-search for the content-hub-backed firehose widgets (fin-x,
 * stocks-news, truth-social). Compiles the header query + the widget's
 * `backend_source`(s) into a `ContentHubFilter` (via `buildContentHubTextFilter`)
 * and runs it through the same `useContentHubFirehoseSearch` adapter the funnel
 * panel already uses — so the header box, the panel, and the base feed all share
 * one content-hub pipeline (cursor-paginated, live-updating).
 *
 * Results render exactly as before (`FirehoseHighlightItem` in a grouped
 * virtuoso overlay). A From/To date+time range narrows results on the
 * `occurs_at` column: local picker values are converted to UTC ISO strings and
 * compiled into an `occurs_at` leaf that AND-s into the same tree. Either the
 * text query or a date bound (or both) is enough to run a search.
 */
function ContentHubFirehoseSearchForm(props: ContentHubFirehoseSearchProps) {
  const {
    setTitle,
    formOpen = true,
    persistKey,
    onActiveChange,
    sources,
    mapItem,
    defaultTitle = "Firehose",
  } = props;

  const [savedSearch, setSavedSearch] = useWidgetSearchState(persistKey);
  const [query, setQuery] = useState<string>(savedSearch.query ?? "");
  const [exactMatch, setExactMatch] = useState<boolean>(
    savedSearch.exactMatch ?? false
  );

  const [startDate, setStartDate] = useState<string>(savedSearch.startDate ?? "");
  const [startTime, setStartTime] = useState<string>(
    savedSearch.startTime ?? "00:00"
  );
  const [endDate, setEndDate] = useState<string>(savedSearch.endDate ?? "");
  const [endTime, setEndTime] = useState<string>(savedSearch.endTime ?? "23:59");

  // The committed criteria that actually drive the search (Enter / Search / a
  // persisted search re-applied on mount). Typing / picking alone doesn't fetch.
  const [activeQuery, setActiveQuery] = useState<string>(savedSearch.query ?? "");
  const [activeExactMatch, setActiveExactMatch] = useState<boolean>(
    savedSearch.exactMatch ?? false
  );
  const [activeDateFrom, setActiveDateFrom] = useState<string | undefined>(
    undefined
  );
  const [activeDateTo, setActiveDateTo] = useState<string | undefined>(undefined);

  const formRef = React.useRef<HTMLDivElement>(null);
  const [formHeight, setFormHeight] = useState<number>(40);

  const filter = useMemo(
    () =>
      buildContentHubTextFilter({
        query: activeQuery,
        sources,
        exactMatch: activeExactMatch,
        dateFrom: activeDateFrom,
        dateTo: activeDateTo,
      }),
    [activeQuery, sources, activeExactMatch, activeDateFrom, activeDateTo]
  );

  const hasCriteria =
    !!activeQuery.trim() || !!activeDateFrom || !!activeDateTo;

  const { stories, isLoading, isError, hasMore, loadNextPage, loadingNextPage } =
    useContentHubFirehoseSearch(filter, { enabled: hasCriteria, mapItem });

  const showSearchResults = hasCriteria;

  // Convert a local date ("YYYY-MM-DD") + time ("HH:MM") into a UTC ISO string.
  // The start bound floors to the minute; the end bound fills to :59.999 so the
  // whole end minute is inclusive. Empty date → undefined (open-ended side).
  const toISO = (date: string, time: string, isEnd: boolean): string | undefined => {
    if (!date) return undefined;
    const [h, m] = (time || (isEnd ? "23:59" : "00:00")).split(":").map(Number);
    const d = new Date(date);
    d.setHours(h, m, isEnd ? 59 : 0, isEnd ? 999 : 0);
    return d.toISOString();
  };

  // Commit current inputs as the active search criteria.
  const commitSearch = (q: string) => {
    setActiveQuery(q);
    setActiveExactMatch(exactMatch);
    setActiveDateFrom(toISO(startDate, startTime, false));
    setActiveDateTo(toISO(endDate, endTime, true));
  };

  const handleSearch = (q: string) => {
    // Require a text query OR a date bound before searching.
    if (!q.trim() && !startDate && !endDate) return;
    commitSearch(q);
    setSavedSearch({ query: q, exactMatch, startDate, startTime, endDate, endTime });
    const activeFilters: string[] = [];
    if (exactMatch) activeFilters.push("exact_match");
    if (startDate || endDate) activeFilters.push("date_range");
    trackSearchSubmit({
      search_term: q,
      search_context: "firehose",
      search_filters:
        activeFilters.length > 0 ? activeFilters.join(", ") : undefined,
    });
  };

  const handleClear = () => {
    setQuery("");
    setExactMatch(false);
    setStartDate("");
    setEndDate("");
    setActiveQuery("");
    setActiveExactMatch(false);
    setActiveDateFrom(undefined);
    setActiveDateTo(undefined);
    setSavedSearch({});
  };

  // Re-apply a persisted search on mount (after Done / tab switch / reload).
  useEffect(() => {
    if (
      savedSearch.query?.trim() ||
      savedSearch.startDate ||
      savedSearch.endDate
    ) {
      commitSearch(savedSearch.query ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showSearchResults && stories.length > 0 && activeQuery) {
      trackSearchResults({
        search_term: activeQuery,
        search_context: "firehose",
        results_count: stories.length,
      });
    }
  }, [stories.length, activeQuery, showSearchResults]);

  useEffect(() => {
    if (showSearchResults) {
      setTitle?.(
        activeQuery
          ? `Showing results for '${activeQuery}'`
          : "Showing search results"
      );
    } else {
      setTitle?.(defaultTitle);
    }
    return () => setTitle?.(defaultTitle);
  }, [showSearchResults, activeQuery, setTitle, defaultTitle]);

  // Keep the container mounting this form while a search is in play (after Done),
  // and let it drop once the search is cleared.
  useEffect(() => {
    onActiveChange?.(showSearchResults);
  }, [showSearchResults, onActiveChange]);

  // Measure form height so the results overlay sits just below it.
  useEffect(() => {
    const update = () => {
      if (formRef.current) setFormHeight(formRef.current.offsetHeight);
    };
    update();
    const resizeObserver = new ResizeObserver(update);
    if (formRef.current) resizeObserver.observe(formRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <React.Fragment>
      <div
        className={`bg-gray-800 border-b border-gray-700/50 px-3 py-2 space-y-2${
          formOpen ? "" : " hidden"
        }`}
        ref={formRef}
      >
        <SearchBar
          value={query}
          onChange={setQuery}
          loading={isLoading}
          onSearch={handleSearch}
          onClear={handleClear}
        />

        {/* Options row: exact match + date/time range + actions. Sticky so they stay reachable on a short widget. */}
        <div className="sticky bottom-0 z-10 bg-gray-800 flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={exactMatch}
              onChange={(e) => setExactMatch(e.target.checked)}
              className="size-3.5 rounded border-gray-600 bg-gray-900 text-squawk-blue focus:ring-0 focus:ring-offset-0"
            />
            <span className="text-xs text-gray-400">Exact match</span>
          </label>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            {(query || startDate || endDate || showSearchResults) && (
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-1 text-xs font-medium rounded text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => handleSearch(query)}
              disabled={!query.trim() && !startDate && !endDate}
              className="px-3 py-1 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Search
            </button>
            {props.onClose && (
              <button
                onClick={props.onClose}
                className="px-3 py-1 text-xs font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>

      {showSearchResults &&
        (stories.length > 0 ? (
          <section
            className="absolute flex-1 z-30 bg-white dark:bg-gray-900 inset-0 pb-[40px]"
            style={{ top: formOpen ? `${formHeight}px` : 0 }}
          >
            <VirtuosoWrapper
              data={stories}
              grouping={{
                groupBy: (items) =>
                  GroupingUtils.groupByDate(
                    items,
                    (item) => getDisplayTime(item),
                    { includeYesterday: true }
                  ),
                hideFirstGroupHeader: false,
              }}
              scrollManager={{ enabled: false }}
              pagination={{
                hasMore,
                isLoadingMore: loadingNextPage,
                loadNext: loadNextPage,
              }}
              renderItem={(item) => <FirehoseHighlightItem item={item} />}
              renderGroupHeader={(title) => <DateSectionHeader title={title} />}
              className="scrollbar-thin absolute inset-0"
            />
          </section>
        ) : (
          <section
            className="absolute flex-1 z-30 bg-white dark:bg-gray-900 inset-0 pb-[40px]"
            style={{ top: formOpen ? `${formHeight}px` : 0 }}
          >
            {/* While the search is pending, EmptyPlaceholder shows a spinner
                (after a short delay); once it settles it shows the message. */}
            <EmptyPlaceholder
              message={isError ? "Search failed. Please try again." : "No stories found"}
              isLoading={isLoading}
            />
          </section>
        ))}
    </React.Fragment>
  );
}

export default ContentHubFirehoseSearchForm;
